"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ProductFocus, getFlowViewSteps, type OnboardingViewType } from "@/constants/onboarding";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { useOnboardingModal } from "@/contexts/onboarding/OnboardingModalContext";
import { GailProductRoles } from "@/enums/user";
import { useAlert } from "@/hooks/useAlert";
import { useOrg } from "@/hooks/useOrg";
import type { OrgOnboardingDetails } from "@/types/org";
import { apiFetch } from "@/lib/api";
import {
  ConfigurationStatus,
  detectExistingConfiguration,
  getStepsToSkip,
} from "@/utils/onboarding/configDetection";
import { shouldOfferPhoneVerificationStep } from "@/utils/verification/missingTwilioFields";
import { AfterHoursStep } from "./steps/AfterHoursStep";
import { BusinessHoursStep } from "./steps/BusinessHoursStep";
import { CallConcurrencyStep } from "./steps/CallConcurrencyStep";
import { CampaignSetupStep } from "./steps/CampaignSetupStep";
import { CompanyInfoStep } from "./steps/CompanyInfoStep";
import { ConfirmExitStep } from "./steps/ConfirmExitStep";
import { ConfirmNotificationsStep } from "./steps/ConfirmNotificationsStep";
import { ConfigureGailGptStep } from "./steps/configure-gailgpt";
import { DirectorySetupStep } from "./steps/DirectorySetupStep";
import { FocusSelectionStep } from "./steps/FocusSelectionStep";
import { InboundScriptStep } from "./steps/InboundScriptStep";
import { LanguageSelectionStep } from "./steps/LanguageSelectionStep";
import { OutboundScriptStep } from "./steps/OutboundScriptStep";
import { PhoneVerificationStep } from "./steps/PhoneVerificationStep";
import { SetupAndTryGailStep } from "./steps/SetupAndTryGailStep";

const viewTransition = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const },
};

type ViewType = OnboardingViewType;

// getFlowViewSteps is now imported from @/constants/onboarding (single source of truth)

// Detect existing configuration if not already detected
const detectConfigIfNeeded = (
  org: Parameters<typeof detectExistingConfiguration>[0] | undefined,
  configStatus: ConfigurationStatus | null,
  setConfigStatus: (status: ConfigurationStatus) => void
) => {
  if (!org || configStatus !== null) return;
  detectExistingConfiguration(org).then((status) => {
    setConfigStatus(status);
  });
};

export function OnboardingModal() {
  const { t } = useTranslation("onboarding");
  const { isOpen, initialStep, closeModal } = useOnboardingModal();
  const [currentView, setCurrentView] = useState<ViewType>("focus-selection");
  const [previousView, setPreviousView] = useState<ViewType>("focus-selection");

  // Flow state
  const [productFocus, setProductFocus] = useState<ProductFocus[]>([]);
  const [configStatus, setConfigStatus] = useState<ConfigurationStatus | null>(null);
  const [flowSteps, setFlowSteps] = useState<OnboardingViewType[]>([]);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);

  // Use case IDs created during directory setup, to associate with the inbound script
  const [pendingUseCaseIds, setPendingUseCaseIds] = useState<string[]>([]);
  const smartSetupEnhancementStartedRef = useRef<string | null>(null);

  const { org, saveOrg, refreshOrg } = useOrg();
  const { setAlert } = useAlert();
  const { productRolesState } = useAuthContext();
  const hasAgentAccess = productRolesState.roles?.includes(GailProductRoles.GailAgentUser) ?? false;

  // Detect existing configuration on mount (only for users with agent access)
  useEffect(() => {
    if (!hasAgentAccess) return;
    detectConfigIfNeeded(org, configStatus, setConfigStatus);
  }, [org, configStatus, hasAgentAccess]);

  useEffect(() => {
    if (!isOpen || !hasAgentAccess || !org?.id) {
      return;
    }

    if (smartSetupEnhancementStartedRef.current === org.id) {
      return;
    }

    smartSetupEnhancementStartedRef.current = org.id;
    apiFetch<{ success: boolean }>(
      "/api/onboarding/smart-setup-enhancements",
      "POST",
      {
      silent: true,
    },
    )
      .then((response) => {
        if (response?.success) {
          return refreshOrg();
        }

        smartSetupEnhancementStartedRef.current = null;
      })
      .catch((error) => {
        smartSetupEnhancementStartedRef.current = null;
        console.error("Failed to apply smart setup enhancements", error);
      });
  }, [
    hasAgentAccess,
    isOpen,
    org?.id,
    refreshOrg,
  ]);

  // Sync productFocus from org data (handles initial load and page refreshes)
  useEffect(() => {
    const gailFocus = org?.details?.onboarding?.gailFocus;
    if (!Array.isArray(gailFocus)) return;
    const mapped = gailFocus
      .filter((f): f is ProductFocus => ["inbound", "outbound", "gailgpt"].includes(f))
      .filter((value, index, self) => self.indexOf(value) === index);
    if (mapped.length > 0) {
      setProductFocus(mapped);
    }
  }, [org?.details?.onboarding?.gailFocus]);

  const onboardingDetails = org?.details?.onboarding;

  const updateOnboardingDetails = useCallback(
    (detailsUpdate: Partial<OrgOnboardingDetails>) => {
      const currentDetails: OrgOnboardingDetails = {
        status: "not_started",
        maxCompletedStep: 1,
        ...onboardingDetails,
      };

      // Fire and forget - no need to await or show loading for onboarding progress
      saveOrg(
        {
          details: {
            ...org?.details,
            onboarding: {
              ...currentDetails,
              ...detailsUpdate,
              updatedAt: new Date().toISOString(),
            },
          },
        },
        { silent: true }
      ).catch((error) => {
        console.error("Failed to update onboarding details", error);
        setAlert({
          status: "error",
          message: t("onboardingUpdateFailed"),
          secondsToClose: 4,
        });
      });
    },
    [onboardingDetails, org?.details, saveOrg, setAlert, t]
  );

  // Handle initialStep from context when modal is opened externally
  useEffect(() => {
    if (isOpen && initialStep) {
      setCurrentView(initialStep);
    }
  }, [isOpen, initialStep]);

  // Reset to focus-selection view when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentView("focus-selection");
    }
  }, [isOpen]);

  // Handler for when user skips/exits onboarding without completing
  const handleSkip = useCallback(() => {
    closeModal();
    updateOnboardingDetails({
      status: "skipped",
    });
  }, [updateOnboardingDetails, closeModal]);

  // Final complete handler
  const handleFinalComplete = useCallback(() => {
    updateOnboardingDetails({
      status: "completed",
      maxCompletedStep: flowSteps.length,
      completedAt: new Date().toISOString(),
    });

    closeModal();
  }, [updateOnboardingDetails, flowSteps.length, closeModal]);

  // Track step index to persist progress (declared early so handleFocusSelectionComplete can access it)
  const prevStepIdxRef = useRef(currentStepIdx);

  const offerPhoneVerification = shouldOfferPhoneVerificationStep(org);

  const buildFlowSteps = useCallback(
    (selections: ProductFocus[]) => {
      const skipSteps = configStatus
        ? getStepsToSkip(configStatus, selections, {
            ...org?.details?.onboarding,
            businessId: org?.details?.businessId,
            twilioBusinessType: org?.details?.twilioBusinessType,
          })
        : {};

      return getFlowViewSteps(selections, skipSteps, offerPhoneVerification);
    },
    [
      configStatus,
      offerPhoneVerification,
      org?.details?.onboarding,
      org?.details?.businessId,
      org?.details?.twilioBusinessType,
    ]
  );

  useEffect(() => {
    if (productFocus.length === 0) {
      return;
    }
    setFlowSteps(buildFlowSteps(productFocus));
  }, [buildFlowSteps, productFocus]);

  // Handle focus selection (first step of the flow)
  const handleFocusSelectionComplete = useCallback(
    (selections: ProductFocus[]) => {
      setProductFocus(selections);

      const steps = buildFlowSteps(selections);
      setFlowSteps(steps);

      // Navigate to first step after focus selection
      const nextStepIdx = steps.indexOf("focus-selection") + 1;
      if (nextStepIdx >= steps.length) {
        handleFinalComplete();
        return;
      }

      // Save product focus and step progress in a single PATCH to avoid a race
      // condition where a second PATCH (from the maxCompletedStep useEffect) could
      // overwrite gailFocus before the first response returns.
      updateOnboardingDetails({
        gailFocus: selections,
        status: "in_progress",
        maxCompletedStep: nextStepIdx,
      });

      // Update the ref BEFORE setting state so the useEffect doesn't fire a duplicate save
      prevStepIdxRef.current = nextStepIdx;
      setCurrentStepIdx(nextStepIdx);
      setCurrentView(steps[nextStepIdx]);
    },
    [buildFlowSteps, updateOnboardingDetails, handleFinalComplete]
  );

  // Navigate to next step
  const handleNext = useCallback(() => {
    const nextIdx = currentStepIdx + 1;
    if (nextIdx >= flowSteps.length || flowSteps[nextIdx] === undefined) {
      handleFinalComplete();
      return;
    }
    if (flowSteps[nextIdx] === "phone-verification" && !offerPhoneVerification) {
      handleFinalComplete();
      return;
    }
    setCurrentStepIdx(nextIdx);
    setCurrentView(flowSteps[nextIdx]);
  }, [currentStepIdx, flowSteps, handleFinalComplete, offerPhoneVerification]);

  // Persist onboarding progress whenever step index advances.
  // This runs AFTER the render, so any org state updates from a step's saveOrg
  // (e.g. CompanyInfoStep saving the website) are already committed, avoiding
  // the stale-closure problem of calling updateOnboardingDetails synchronously
  // inside handleNext.
  useEffect(() => {
    if (currentStepIdx > prevStepIdxRef.current) {
      prevStepIdxRef.current = currentStepIdx;
      updateOnboardingDetails({ maxCompletedStep: currentStepIdx });
    }
  }, [currentStepIdx, updateOnboardingDetails]);

  // Track use cases created during directory setup
  const handleUseCaseCreated = useCallback((useCaseId: string) => {
    setPendingUseCaseIds((prev) => [...prev, useCaseId]);
  }, []);

  // Navigate to previous step
  const handleBack = useCallback(() => {
    const prevIdx = currentStepIdx - 1;
    if (prevIdx >= 0) {
      setCurrentStepIdx(prevIdx);
      setCurrentView(flowSteps[prevIdx]);
    }
  }, [currentStepIdx, flowSteps]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && currentView !== "confirm-exit") {
        setPreviousView(currentView);
        setCurrentView("confirm-exit");
        return;
      }
      if (!open) {
        closeModal();
      }
    },
    [currentView, closeModal]
  );

  const handleCancelExit = useCallback(() => {
    setCurrentView(previousView);
  }, [previousView]);

  // Render the appropriate step based on current view
  const renderCurrentView = (): React.ReactNode => {
    switch (currentView) {
      case "focus-selection":
        return (
          <FocusSelectionStep
            initialSelection={productFocus}
            onComplete={handleFocusSelectionComplete}
          />
        );
      case "company-info":
        return (
          <CompanyInfoStep
            onBack={handleBack}
            onNext={handleNext}
            productFocus={productFocus}
          />
        );
      case "language-selection":
        return <LanguageSelectionStep onBack={handleBack} onNext={handleNext} />;
      case "business-hours":
        return (
          <BusinessHoursStep onBack={handleBack} onNext={handleNext} productFocus={productFocus} />
        );
      case "confirm-notifications":
        return <ConfirmNotificationsStep onBack={handleBack} onNext={handleNext} />;
      case "directory-setup":
        return (
          <DirectorySetupStep
            onBack={handleBack}
            onNext={handleNext}
            onUseCaseCreated={handleUseCaseCreated}
          />
        );
      case "inbound-script":
        return (
          <InboundScriptStep
            onBack={handleBack}
            onNext={handleNext}
            newUseCaseIds={pendingUseCaseIds}
            onUseCaseIdsConsumed={() => setPendingUseCaseIds([])}
          />
        );
      case "after-hours":
        return <AfterHoursStep onBack={handleBack} onNext={handleNext} />;
      case "call-concurrency":
        return <CallConcurrencyStep onBack={handleBack} onNext={handleNext} />;
      case "outbound-script":
        return <OutboundScriptStep onBack={handleBack} onNext={handleNext} />;
      case "campaign-setup":
        return <CampaignSetupStep onBack={handleBack} onNext={handleNext} />;
      case "configure-gailgpt":
        return (
          <ConfigureGailGptStep
            selectedFocus={productFocus}
            onBack={handleBack}
            onNext={handleNext}
            isLastStep={currentStepIdx === flowSteps.length - 1}
          />
        );
      case "call-gail":
        return (
          <SetupAndTryGailStep
            onBack={handleBack}
            onNext={handleNext}
            isLastStep={currentStepIdx === flowSteps.length - 1}
          />
        );
      case "phone-verification":
        return <PhoneVerificationStep onBack={handleBack} onNext={handleNext} />;
      case "confirm-exit":
        return <ConfirmExitStep onConfirm={handleSkip} onCancel={handleCancelExit} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={clsx(
          "bg-panel_secondary",
          "md:min-w-[90vw]",
          "min-h-[40rem] lg:min-w-[48rem] max-h-[90vh]",
          "!rounded-3xl",
          "!overflow-y-scroll",
          "p-0",
          "transition-all duration-300 ease-in-out"
        )}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{t("welcomeSteps.title")}</DialogTitle>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial={viewTransition.initial}
            animate={viewTransition.animate}
            exit={viewTransition.exit}
            transition={viewTransition.transition}
            className="h-full"
          >
            {renderCurrentView()}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
